import { useState, useEffect, useCallback, useMemo } from "react";
import {
  courseService,
  CourseFilter,
  CoursesResponse,
  FilterOptions,
} from "../services/api/courseService";
import { Course } from "../types/course";

interface UseCourseDetailReturn {
  course: Course | null;
  relatedCourses: Course[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useCourseDetail = (courseId: string): UseCourseDetailReturn => {
  const [course, setCourse] = useState<Course | null>(null);
  const [relatedCourses, setRelatedCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCourseDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await courseService.getCourseDetails(courseId);

      if (response.data && response.data.success && response.data.data) {
        setCourse(response.data.data.course);
        setRelatedCourses(response.data.data.relatedCourses || []);
      } else {
        setError(response.data?.message || "Failed to fetch course details");
      }
    } catch (err) {
      console.error("Error fetching course details:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCourseDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const refetch = () => {
    if (courseId) {
      fetchCourseDetail();
    }
  };

  return {
    course,
    relatedCourses,
    loading,
    error,
    refetch,
  };
};

interface UsePopularCoursesReturn {
  courses: Course[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePopularCourses = (): UsePopularCoursesReturn => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPopularCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await courseService.getPopularCourses();

      if (response.data && response.data.success && response.data.data) {
        setCourses(response.data.data || []);
      } else {
        setError(response.data?.message || "Failed to fetch popular courses");
      }
    } catch (err) {
      console.error("Error fetching popular courses:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopularCourses();
  }, []);

  const refetch = () => {
    fetchPopularCourses();
  };

  return {
    courses,
    loading,
    error,
    refetch,
  };
};

// Comprehensive courses hook with pagination and filtering
export interface UseCoursesResult {
  courses: Course[];
  pagination: CoursesResponse["pagination"] | null;
  filters: FilterOptions | null;
  loading: boolean;
  error: string | null;
  currentFilters: CourseFilter;
  searchQuery: string;
  // Actions
  setFilters: (filters: Partial<CourseFilter>) => void;
  setSearchQuery: (query: string) => void;
  clearFilters: () => void;
  goToPage: (page: number) => void;
  refreshCourses: () => void;
  applySearch: () => void;
  // Computed values
  hasFilters: boolean;
  totalCourses: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const DEFAULT_FILTERS: CourseFilter = {
  page: 1,
  limit: 12,
};

/**
 * Custom hook for managing courses with pagination and filtering
 */
export const useCourses = (
  initialFilters?: Partial<CourseFilter>
): UseCoursesResult => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [pagination, setPagination] = useState<
    CoursesResponse["pagination"] | null
  >(null);
  const [availableFilters, setAvailableFilters] =
    useState<FilterOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<CourseFilter>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [searchQuery, setSearchQuery] = useState("");

  /**
   * Handle API errors
   */
  const handleApiError = (err: unknown): string => {
    console.error("API Error:", err);
    if (err instanceof Error) {
      return err.message;
    }
    return "An unexpected error occurred";
  };

  /**
   * Fetch courses with current filters
   */
  const fetchCourses = useCallback(
    async (filters: CourseFilter = currentFilters) => {
      setLoading(true);
      setError(null);

      // Debug logging
      console.log("🔍 Fetching courses with filters:", filters);

      try {
        const response = await courseService.getAllCourses(filters);

        console.log("📊 API Response:", response.data);

        if (response.data && response.data.success) {
          setCourses(response.data.data);
          setPagination(response.data.pagination);
          setAvailableFilters(response.data.filters);
        } else {
          setError(response.data?.message || "Failed to fetch courses");
        }
      } catch (err) {
        const errorMessage = handleApiError(err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [currentFilters]
  );

  /**
   * Search courses (using the same endpoint as getAllCourses with search parameter)
   */
  const searchCourses = useCallback(
    async (query: string, filters: CourseFilter = currentFilters) => {
      if (!query.trim()) {
        return fetchCourses(filters);
      }

      setLoading(true);
      setError(null);

      // Include search in filters for the getAllCourses endpoint
      const searchFilters = {
        ...filters,
        search: query,
      };

      try {
        const response = await courseService.getAllCourses(searchFilters);

        if (response.data && response.data.success) {
          setCourses(response.data.data);
          setPagination(response.data.pagination);
          setAvailableFilters(response.data.filters);
        } else {
          setError(response.data?.message || "Failed to search courses");
        }
      } catch (err) {
        const errorMessage = handleApiError(err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [currentFilters, fetchCourses]
  );

  /**
   * Update filters and fetch courses
   */
  const setFilters = useCallback(
    (newFilters: Partial<CourseFilter>) => {
      console.log("🔧 Setting filters:", newFilters);
      console.log("🔧 Current filters before update:", currentFilters);

      const updatedFilters = {
        ...currentFilters,
        ...newFilters,
        // Only reset page to 1 if it's not a page change
        page: newFilters.page !== undefined ? newFilters.page : 1,
      };

      console.log("🔧 Updated filters:", updatedFilters);
      setCurrentFilters(updatedFilters);
    },
    [currentFilters]
  );

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setCurrentFilters(DEFAULT_FILTERS);
    setSearchQuery("");
  }, []);

  /**
   * Navigate to specific page
   */
  const goToPage = useCallback(
    (page: number) => {
      setFilters({ page });
    },
    [setFilters]
  );

  /**
   * Refresh courses with current filters
   */
  const refreshCourses = useCallback(() => {
    if (searchQuery.trim()) {
      searchCourses(searchQuery);
    } else {
      fetchCourses();
    }
  }, [fetchCourses, searchCourses, searchQuery]);

  /**
   * Apply search with current query
   */
  const applySearch = useCallback(() => {
    if (searchQuery.trim()) {
      searchCourses(searchQuery);
    } else {
      fetchCourses();
    }
  }, [fetchCourses, searchCourses, searchQuery]);

  /**
   * Auto-fetch when filters change
   */
  useEffect(() => {
    if (searchQuery.trim()) {
      searchCourses(searchQuery, currentFilters);
    } else {
      fetchCourses(currentFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters]); // Only depend on currentFilters to avoid infinite loops

  /**
   * Memoized computed values
   */
  const computedValues = useMemo(
    () => ({
      hasFilters: Object.keys(currentFilters).some(
        (key) =>
          key !== "page" &&
          key !== "limit" &&
          currentFilters[key] !== undefined &&
          currentFilters[key] !== null &&
          currentFilters[key] !== ""
      ),
      totalCourses: pagination?.total || 0,
      currentPage: pagination?.page || 1,
      totalPages: pagination?.totalPages || 1,
      hasNextPage: pagination?.hasNext || false,
      hasPrevPage: pagination?.hasPrev || false,
    }),
    [currentFilters, pagination]
  );

  return {
    courses,
    pagination,
    filters: availableFilters,
    loading,
    error,
    currentFilters,
    searchQuery,
    // Actions
    setFilters,
    setSearchQuery,
    clearFilters,
    goToPage,
    refreshCourses,
    applySearch,
    // Computed values
    ...computedValues,
  };
};

export default useCourses;
